<template>
  <StepLayout
    :back="back"
    :save="save"
    :next="next"
    :isEditRoute="true"
    buttonDataEc="envelopes_reminders_page"
  >
    <template v-slot:title>
      {{ $t('ui.envelopes.create.steps.reminders.title') }}
    </template>
    <p class="text-black">{{ $t('ui.envelopes.create.steps.reminders.description') }}</p>

    <div
      class="flex flex-col sm:grid-flow-col sm:auto-cols-max sm:grid sm:grid-rows-auto4 sm:gap-x-xxl gap-xs mb-base"
    >
      <div
        v-for="option in reminderOptions"
        class="flex items-center first:col-span-2 mb-sm first:mb-twenty"
        :key="option.value"
      >
        <VCheckbox
          :id="`reminder.${option.value}`"
          name="reminder"
          :checked="reminder === option.value"
          @input="updateReminder(option.value)"
          type="radio"
          data-ec="envelopes_reminders_page"
          :data-et="`radio_option_${option.value}`"
        />
        <label
          :for="`reminder.${option.value}`"
          class="cursor-pointer pl-base"
        >
          {{ option.option }}
        </label>
      </div>
    </div>

    <HeightTransition>
      <FloatingLabelInput
        v-if="isCustomReminder"
        class="mt-base"
        type="number"
        :min="0"
        :step="1"
        :value="customReminder"
        @input="updateCustomReminder"
        :fieldIsRequired="false"
        :placeholder="$t('ui.envelopes.create.steps.reminders.customInterval')"
        :aria-label="$t('ui.envelopes.create.steps.reminders.customInterval')"
        data-test="reminders-custom_interval"
        data-hj-suppress
        data-ec="envelopes_reminders_page"
        data-et="custom_interval_input"
      />
    </HeightTransition>
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import {
  useEnvelope,
  useEnvelopesStepsNavigation,
} from '@/composables/envelopes';
import i18n from '@/locales';

import VCheckbox from '@/components/deprecated/atoms/Checkbox.vue';
import StepLayout from '@/components/layouts/StepLayout';
import FloatingLabelInput from '@/components/tailwind/forms/FloatingLabelInput.vue';
import HeightTransition from '@/transitions/HeightTransition.vue';

const route = useRoute();

const { envelope } = useEnvelope(route.params.fingerprint);

// initialise local state
const reminderOptions = [
  {
    value: 0,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.noReminders'),
  },
  { value: 1, option: i18n.t('ui.envelopes.create.steps.reminders.options.everyday') },
  {
    value: 2,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every2Days'),
  },
  {
    value: 3,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every3Days'),
  },
  {
    value: 5,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every5Days'),
  },
  {
    value: 7,
    option: i18n.t('ui.envelopes.create.steps.reminders.options.every7Days'),
  },
  {
    value: 'custom',
    option: i18n.t('ui.envelopes.create.steps.reminders.options.custom'),
  },
];
const reminderValues = reminderOptions.map(option => option.value);

const reminderDays = computed(() =>
  Math.floor(envelope.value.auto_remind_hours / 24));
const customReminder = ref(!reminderValues.includes(reminderDays.value) ? reminderDays.value : null);

/** @type {import('vue').Ref<'custom' | number>} */
const reminder = ref(customReminder.value ? 'custom' : reminderDays.value);

const updateValue = computed(() => ({
  envelope_reminder_interval:
    reminder.value !== 'custom'
      ? reminder.value * 24
      : customReminder.value * 24,
}));
const isValid = computed(() => reminder.value !== 'custom' || customReminder.value > 0);

// action param is temporary, will be replaced when tanstack query is implemented
const { back, save, next } = useEnvelopesStepsNavigation(
  'envelopes/updateEnvelopes',
  updateValue,
  isValid,
);

const isCustomReminder = computed(() => reminder.value === 'custom');

/** @param {string} value */
function updateCustomReminder(value) {
  customReminder.value = parseInt(value, 10);
}

function updateReminder(value) {
  customReminder.value = null;
  reminder.value = value;
}
</script>

